import React from 'react';
import { withStyles } from '@material-ui/core';
import styles from './CreditNoteDetails.styles';
import { Cancel as CancelIcon, CheckCircle } from '@markinson/uicomponents-v2/SvgIcons/';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import CreditNoteDetailsForm from './CreditNoteDetails.form';
import FormView from 'components/FormView';
import ICreditNoteDetailsProperties, { changeFormFieldNames, ICreditNoteDetailsHandle } from './CreditNoteDetails.properties';
import { useCNContextSelector } from '../CreditNotes.context';
import { useFetchCreditNoteDetails, useSetCreditNoteDetailsQueryData, useCalculateCreditNoteDetails, useUpdateCreditNoteDetails } from 'api/creditNotes/creditNotes';
import { objectifyAddressForPost } from 'api/utils';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';

const CreditNoteDetails = (props: ICreditNoteDetailsProperties, ref: React.Ref<ICreditNoteDetailsHandle>) => {
    const { classes, changeSelectedTab, values, resetForm, changeConfirmationDialog, dirty } = props;
    const { setActionBar } = React.useContext(ActionBarContext);
    const CreditNoteId = useCNContextSelector<'CreditNote'>((state) => state.CreditNote);
    const CreditNoteContext = useCNContextSelector<'CreditNotesContext'>((state) => state.CreditNotesContext);
    const { data: creditNoteDetails, isLoading: isCreditNoteDetailsLoading, isFetching: isCreditNoteDetailsFetching } = useFetchCreditNoteDetails(CreditNoteId);
    const setCreditNoteDetails = useSetCreditNoteDetailsQueryData();
    const calculateCreditNoteDetailsMutation = useCalculateCreditNoteDetails();
    const updateCreditNoteDetailsMutation = useUpdateCreditNoteDetails();

    const [internalDirty, setInternalDirty] = React.useState<boolean>(false);
    const [termFieldValidation, setTermFieldValidation] = React.useState<any>(null);
    const isDirty = internalDirty || dirty;

    const rightIcons = React.useMemo(
        () => [
            {
                label: 'Cancel',
                Icon: CancelIcon,
                iconStyle: { fill: 'rgba(178, 0, 0, 1)' },
                action: 'CreditNoteDetailsCancel',
                onCallback: onCancel
            },
            {
                label: 'OK',
                Icon: CheckCircle,
                action: 'CreditNoteDetailsOk',
                onCallback: onOk
            },
        ],
        []
    );

    const onOk = React.useCallback(
        async () => {
            const response = await updateCreditNoteDetails(values);
            if (response?.Status) {
                resetForm();
                changeSelectedTab('Lines');

                return;
            }
        },
        [values, isDirty]
    );

    const onCancel = React.useCallback(
        async () => {
            if (isDirty) {
                changeConfirmationDialog({
                    open: true,
                    title: 'Discard changes',
                    message: 'Are you sure you want to continue?',
                    okLabel: 'Discard',
                    onCancel: () => undefined,
                    onOk: () => {
                        setInternalDirty(false);
                        resetForm();
                        setTimeout(
                            () => {
                                changeSelectedTab('Lines');
                            },
                            0);
                    }
                });
            } else {
                resetForm();
                changeSelectedTab('Lines');
            }

        },
        [values, isDirty]
    );

    React.useImperativeHandle(
        ref,
        () => ({
            CreditNoteDetailsOk: onOk,
            CreditNoteDetailsCancel: onCancel,
            isChangesSavedOrDiscarded(): boolean {
                if (isDirty) {
                    showSnackBar({ variant: 'warning', message: 'Please save or discard changes.' });
                }

                return !isDirty;
            },
        }),
        [onCancel, onOk, changeSelectedTab, isDirty]
    );

    React.useEffect(
        () => {
            setActionBar({
                leftIcons: [],
                centerIcons: [],
                rightIcons
            });
        },
        []
    );

    async function formFieldOnBlur(fieldName: string, targetValue: any): Promise<void> {
        setInternalDirty(true);
        if (!changeFormFieldNames.includes(fieldName)) {

            return;
        }

        const initialValues = creditNoteDetails?.CreditNoteDetails?.inlineObject;

        if (fieldName) {
            const isQuantityField = fieldName.includes('Terms') || fieldName.includes('Age');
            const isValueChanged = isQuantityField ? (parseFloat(targetValue) ? (parseFloat(targetValue) !== parseFloat(initialValues[fieldName])) : parseFloat(initialValues[fieldName]) !== 0) : (targetValue !== initialValues[fieldName]);

            const isInvalidQuantity = isQuantityField && (targetValue === null || targetValue === '');
            if (isQuantityField && isInvalidQuantity && isValueChanged) {
                setCreditNoteDetails(CreditNoteId, { ...initialValues, [fieldName]: 0 });
            }

            if (isValueChanged) {
                setInternalDirty(true);
                if (isInvalidQuantity) {
                    await calculateLineDetailsAndValidate({ ...initialValues, ...values, [fieldName]: 0 }, fieldName as keyof any);
                } else {
                    await calculateLineDetailsAndValidate({ ...initialValues, ...values, [fieldName]: targetValue }, fieldName as keyof any);
                }
            }
        }
    }

    async function calculateLineDetailsAndValidate(line: any, changedField: keyof any): Promise<any> {
        if (CreditNoteId >= 0) {
            const response = await calculateCreditNoteDetailsMutation.mutateAsync({
                ...line,
                WorkCreditNoteId: CreditNoteId,
                ...objectifyAddressForPost(line?.DeliveryAddress, 'Delivery', ['Name', 'Address1', 'Address2', 'City', 'State', 'PostCode', 'Country']),
                ...objectifyAddressForPost(line?.PostalAddress, 'Postal', ['Name', 'Address1', 'Address2', 'City', 'State', 'PostCode', 'Country']),
                urlQueryParams: {
                    ChangedField: changedField
                }
            });

            if (response?.Status) {
                if (response?.CreditNoteDetails) {
                    resetForm();
                    setCreditNoteDetails(CreditNoteId, response);
                }
                setInternalDirty(true);
            }

            if (response?.Status && response?.Forms?.length && changedField === 'Terms') {
                setTermFieldValidation({
                    error: true,
                    helperText: 'Invalid terms for this customer'
                    // helperText: response?.Forms[0]?.Message || 'Invalid term' // Uncomment this line if you want to show API message
                });
            } else if (response?.Status && !response?.Forms && changedField === 'Terms') {
                setTermFieldValidation(null);
            }

            return response;
        } else {
            return null;
        }
    }
    async function updateCreditNoteDetails(val: any): Promise<any> {
        if (CreditNoteId >= 0) {
            const response = await updateCreditNoteDetailsMutation.mutateAsync({
                ...val,
                WorkCreditNoteId: CreditNoteId,
                ...objectifyAddressForPost(val?.DeliveryAddress, 'Delivery', ['Name', 'Address1', 'Address2', 'City', 'State', 'PostCode', 'Country']),
                ...objectifyAddressForPost(val?.PostalAddress, 'Postal', ['Name', 'Address1', 'Address2', 'City', 'State', 'PostCode', 'Country']),
            });

            if (response?.Status) {
                if (response?.CreditNoteDetails) {
                    resetForm();
                    setCreditNoteDetails(CreditNoteId, response);
                }
                setInternalDirty(false);
            }

            return response;
        } else {
            return null;
        }
    }

    async function onSiteCodeChanged(siteCode: any): Promise<void> {
        if (siteCode && siteCode.Code) {
            const initialValues = creditNoteDetails?.CreditNoteDetails?.inlineObject;
            if (siteCode.Code.toUpperCase() !== initialValues.SiteCode.toUpperCase()) {
                setInternalDirty(true);
                await calculateLineDetailsAndValidate({ ...initialValues, ...values, SiteCode: siteCode.Code }, 'SiteCode' as keyof any);
            }
        }
    }

    return (
        <div className={classes.formContainer}>
            <FormView
                isLoading={isCreditNoteDetailsLoading || isCreditNoteDetailsFetching}
                formName={'CreditNoteDetailsForm'}
                schema={CreditNoteDetailsForm}
                includeTabsHeight={false}
                initialValues={creditNoteDetails?.CreditNoteDetails?.inlineObject}
                valuesSchema={creditNoteDetails?.CreditNoteDetails?.schema}
                operationMode={'EDIT'}
                onBlur={(e) => formFieldOnBlur(e?.target?.name, e?.target?.value)}
                customerId={Number(CreditNoteContext?.CustomerId)}
                fieldExtensions={{
                    SiteCode: {
                        onSelectedItemChange: (item, searchText) => onSiteCodeChanged(item ?? searchText)
                    },
                    PostalAddress: {
                        disabled: !creditNoteDetails?.CreditNoteDetails?.inlineObject?.EnablePostalAddress
                    },
                    Terms: {
                        error: termFieldValidation?.error || undefined,
                        helperText: termFieldValidation?.helperText || undefined
                    }
                }}
            />
        </div>
    );
};

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(CreditNoteDetails)));
